import { Loader2, Plus, Tags } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PromptEditorDialog } from "@/components/composer/prompts/PromptEditorDialog";
import { PromptTagsManager } from "@/components/composer/prompts/PromptTagsManager";
import { PromptRow, SortableMineRow } from "@/components/composer/prompts/PromptRow";
import { PromptFillInDialog } from "@/components/composer/prompts/PromptFillInDialog";
import { usePromptsPanel } from "./usePromptsPanel";

export const PromptsPanel = () => {
  const s = usePromptsPanel();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search prompts…"
          value={s.search}
          onChange={(e) => s.setSearch(e.target.value)}
          className="h-8 max-w-xs"
        />
        {s.isOwner && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => s.setTagsManagerOpen(true)}>
              <Tags className="h-3.5 w-3.5" /> Manage tags
            </Button>
            <Button size="sm" onClick={() => s.setCreating(true)}>
              <Plus className="h-3.5 w-3.5" /> New prompt
            </Button>
          </div>
        )}
      </div>

      {s.tabTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Tags:</span>
          <Button
            type="button"
            size="sm"
            variant={s.tagFilter === null ? "secondary" : "ghost"}
            className="h-6 px-2 text-[11px]"
            onClick={() => s.setTagFilter(null)}
          >
            All
          </Button>
          {s.tabTags.map((t) => (
            <Button
              key={t}
              type="button"
              size="sm"
              variant={s.tagFilter === t ? "secondary" : "ghost"}
              className="h-6 px-2 text-[11px]"
              onClick={() => s.setTagFilter((cur) => (cur === t ? null : t))}
            >
              {t}
            </Button>
          ))}
        </div>
      )}

      <Tabs value={s.tab} onValueChange={(v) => s.setTab(v as "excerptus" | "mine")} className="flex flex-1 flex-col">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="excerptus">Excerptus Prompts</TabsTrigger>
          <TabsTrigger value="mine">My Prompts</TabsTrigger>
        </TabsList>

        <TabsContent value="excerptus" className="flex-1 space-y-3 overflow-auto pr-1">
          {s.loading ? (
            <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : s.filteredExcerptus.length === 0 ? (
            <p className="rounded-md border border-dashed border-border/60 p-3 text-xs text-muted-foreground">
              No published Excerptus prompts yet.
            </p>
          ) : (
            s.filteredExcerptus.map((p) => (
              <PromptRow
                key={p.id}
                badgeKind="excerptus"
                title={p.title}
                body={p.body}
                description={p.description}
                tags={p.tags ?? []}
                modelHint={p.model_hint}
                badge="Excerptus"
                onPushToChat={() => s.handlePushAndRun(p)}
                onCopy={() => s.handleCopy(p.body)}
                onDuplicate={s.isOwner ? () => s.handleDuplicate(p) : undefined}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="mine" className="flex-1 space-y-3 overflow-auto pr-1">
          {s.loading ? (
            <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : s.filteredMine.length === 0 ? (
            <p className="rounded-md border border-dashed border-border/60 p-3 text-xs text-muted-foreground">
              Add a new prompt. Paste a Claude/ChatGPT prompt directly into the body — it's
              preserved verbatim.
            </p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={s.handleDragEnd}>
              <SortableContext items={s.filteredMine.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-3">
                  {s.filteredMine.map((p) => (
                    <SortableMineRow key={p.id} prompt={p}>
                      {(dragProps) => (
                        <PromptRow
                          badgeKind="mine"
                          title={p.title}
                          body={p.body}
                          description={p.description}
                          tags={p.tags ?? []}
                          modelHint={p.model_hint}
                          badge="Mine"
                          dragHandleProps={s.isOwner ? dragProps : undefined}
                          onPushToChat={() => s.handlePushAndRun(p)}
                          onCopy={() => s.handleCopy(p.body)}
                          onEdit={s.isOwner ? () => s.setEditing(p) : undefined}
                          onDelete={s.isOwner ? () => s.handleDelete(p) : undefined}
                        />
                      )}
                    </SortableMineRow>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </TabsContent>
      </Tabs>

      {s.active && (
        <PromptEditorDialog
          open={s.creating}
          onOpenChange={(o) => s.setCreating(o)}
          workspaceId={s.active.id}
          onSaved={(p) => s.setMine((cur) => [...cur, p])}
        />
      )}
      {s.editing && s.active && (
        <PromptEditorDialog
          open={Boolean(s.editing)}
          onOpenChange={(o) => !o && s.setEditing(null)}
          workspaceId={s.active.id}
          prompt={s.editing}
          onSaved={() => {
            s.setEditing(null);
            void s.refresh();
          }}
        />
      )}
      {s.active && (
        <PromptTagsManager
          open={s.tagsManagerOpen}
          onOpenChange={s.setTagsManagerOpen}
          workspaceId={s.active.id}
          onChanged={() => void s.refresh()}
        />
      )}
      {s.fillIn && (
        <PromptFillInDialog
          open={Boolean(s.fillIn)}
          onOpenChange={(o) => !o && s.setFillIn(null)}
          promptTitle={s.fillIn.prompt.title}
          variables={s.fillIn.vars}
          onRun={s.handleFillInRun}
        />
      )}
    </div>
  );
};

export default PromptsPanel;
